import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import clsx from "clsx";
import type { RecordingSyncStatus } from "../../api/types";

type RecordingSyncRowProps = {
  status: RecordingSyncStatus;
  onRetry: () => Promise<void>;
};

const RecordingSyncRow = ({ status, onRetry }: RecordingSyncRowProps) => {
  const { t } = useTranslation();
  const [retrying, setRetrying] = useState(false);

  if (status.status === "idle") {
    return null;
  }

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetry();
    } catch {
      // Leave the existing failure banner visible so the user can retry again.
    } finally {
      setRetrying(false);
    }
  };

  const progressFraction =
    status.status === "syncing"
      ? status.progress.total_files === 0
        ? 0
        : status.progress.uploaded_files / status.progress.total_files
      : status.status === "finalizing"
        ? status.progress.total_screenshots === 0
          ? 0
          : status.progress.processed_screenshots / status.progress.total_screenshots
        : 0;
  const syncPercent = Math.min(100, Math.round(progressFraction * 100));
  const inProgress = status.status === "syncing" || status.status === "finalizing";

  return (
    <div
      className={clsx(
        "mb-3 rounded-xl border px-4 py-3",
        status.status === "failed"
          ? "border-danger/40 bg-danger/10 text-danger"
          : "border-info/40 bg-info/10 text-info",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {status.status === "failed"
              ? t("sync_failed")
              : status.status === "finalizing"
                ? t("finalizing_recording")
                : t("syncing_recording")}
          </p>
          <p className="mt-0.5 break-words text-xs text-text-dim">
            {status.status === "failed"
              ? status.error_message
              : status.status === "finalizing"
                ? t("redacting_screenshots")
                : t("uploading_recording_data")}
          </p>
        </div>
        {inProgress && <span className="shrink-0 text-sm font-medium">{syncPercent}%</span>}
        {status.status === "failed" && (
          <button
            type="button"
            disabled={retrying}
            onClick={handleRetry}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-1.5 text-xs font-medium text-danger transition hover:bg-danger/15 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={14} className={retrying ? "animate-spin" : undefined} />
            {retrying ? t("syncing") : t("retry")}
          </button>
        )}
      </div>
      {inProgress && (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted-surface">
          <div
            className="h-full rounded-full bg-info transition-all"
            style={{ width: `${syncPercent}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default RecordingSyncRow;
